import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { useAPI } from 'hooks/api/useAPI';

import { Stream } from 'types/stream';
import APIError from 'types/api_error';

import {
  updateQueriesData,
  UpdateQueriesDataContext,
} from 'services/update_queries_data';

export function useDeleteStreams({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<
  void,
  APIError,
  { stream_ids: string[] },
  UpdateQueriesDataContext<Stream> | void
> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ stream_ids }) => {
      await Promise.all(
        stream_ids.map((id) =>
          client.delete(`/apps/${applicationID}/streams/${id}`)
        )
      );
    },
    {
      ...options,
      async onMutate({ stream_ids }) {
        await queryClient.cancelQueries(['streams']);
        await queryClient.cancelQueries(['stream']);

        onMutate?.({ stream_ids });

        return updateQueriesData<Stream>({
          queryClient,
          listQueryKey: ['streams'],
          singleQueryKey: ['stream'],
          ids: stream_ids,
          updateData: null,
        });
      },
      onError(error, params, context) {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      async onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['streams']);
        queryClient.invalidateQueries(['stream']);
      },
    }
  );
}
